import { ConfirmDialog } from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { errorCodeEmphasis, errorCodeLabel, isHeartbeatStale } from "@/lib/jobs";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useCancelJob } from "./mutations";
import { durationMs, useJob } from "./queries";

function formatMs(ms: number | null): string {
  if (ms == null) return "—";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function formatTs(ts: string | null | undefined): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleString();
}

/** Returns a human-readable relative age string, e.g. "2m ago" or "45s ago". */
function relativeAge(ts: string): string {
  const diffMs = Date.now() - new Date(ts).getTime();
  const s = Math.max(0, Math.floor(diffMs / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

export function JobDetailPage() {
  const { jobId } = useParams({ from: "/jobs/$jobId" });
  const { data: j } = useJob(jobId);
  const [cancelOpen, setCancelOpen] = useState(false);
  const cancel = useCancelJob();

  if (!j) return null;

  const stale = isHeartbeatStale(j.status, j.heartbeat_at ?? null);

  return (
    <>
      <PageHeader
        title={j.id}
        subtitle={j.kind}
        actions={
          <Button
            variant="danger"
            disabled={j.status !== "running" && j.status !== "queued"}
            onClick={() => setCancelOpen(true)}
          >
            Cancel
          </Button>
        }
      />
      <div className="grid gap-4">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-surface p-4">
          <StatusBadge status={j.status} />
          {stale && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
              heartbeat stale
            </span>
          )}
          {j.progress != null && (
            <>
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-surface-2">
                <div
                  className="h-full rounded-full bg-info"
                  style={{ width: `${j.progress * 100}%` }}
                />
              </div>
              <span className="mono text-xs text-fg-muted tnum">
                {Math.round(j.progress * 100)}%
              </span>
            </>
          )}
        </div>

        {j.error_code && <ErrorCodeBanner code={j.error_code} />}

        <div className="rounded-lg border border-border bg-surface">
          <div className="border-b border-border px-4 py-2.5 text-xs font-medium">Details</div>
          <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 p-4 text-xs">
            <dt className="text-fg-muted">Progress</dt>
            <dd className="mono">{j.progress_message ?? "—"}</dd>
            <dt className="text-fg-muted">Target</dt>
            <dd className="mono">
              {j.target_kind ? `${j.target_kind} · ${j.target_id ?? "—"}` : "—"}
            </dd>
            <dt className="text-fg-muted">Worker</dt>
            <dd className="mono">{j.worker_id ?? "—"}</dd>
            {j.heartbeat_at && (
              <>
                <dt className="text-fg-muted">Last heartbeat</dt>
                <dd className="mono" title={new Date(j.heartbeat_at).toLocaleString()}>
                  {relativeAge(j.heartbeat_at)}
                </dd>
              </>
            )}
            <dt className="text-fg-muted">Queued at</dt>
            <dd className="mono">{formatTs(j.queued_at)}</dd>
            <dt className="text-fg-muted">Started at</dt>
            <dd className="mono">{formatTs(j.started_at)}</dd>
            <dt className="text-fg-muted">Finished at</dt>
            <dd className="mono">{formatTs(j.finished_at)}</dd>
            <dt className="text-fg-muted">Duration</dt>
            <dd className="mono">{formatMs(durationMs(j))}</dd>
            {j.error_message && (
              <>
                <dt className="text-fg-muted">Error</dt>
                <dd className="mono text-danger whitespace-pre-wrap">{j.error_message}</dd>
              </>
            )}
          </dl>
        </div>
      </div>
      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={`Cancel job ${j.id}?`}
        description="The worker will attempt to stop the job at the next checkpoint. Partial artifacts may be discarded."
        confirmLabel="Cancel job"
        tone="danger"
        busy={cancel.isPending}
        onConfirm={async () => {
          await cancel.mutateAsync(j.id);
          setCancelOpen(false);
        }}
      />
    </>
  );
}

function ErrorCodeBanner({ code }: { code: string }) {
  const emphasis = errorCodeEmphasis(code);
  const label = errorCodeLabel(code);

  if (emphasis === "rose") {
    return (
      <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-[7px] border border-rose-300 bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
            {label}
          </span>
          <span>Worker lost — job may need to be retried.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-surface-2 p-4 text-sm text-fg-muted">
      <span className="inline-flex items-center rounded-[7px] border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600">
        {label}
      </span>
    </div>
  );
}
